import type {
  SlideBlockType,
  SlideContentBlock,
  SlideLeafBlock,
  TrainingSlideDef,
} from '@hcc/types';
import { parseSlideBody } from '../../../../lib/slideMarkup';
import { genId } from '../ids';

/** Pure helpers for editing a slide's content-block list. */

export function makeBlock(type: SlideBlockType): SlideContentBlock {
  const id = genId('blk');
  switch (type) {
    case 'heading':
      return { id, type, text: '' };
    case 'text':
      return { id, type, text: '' };
    case 'list':
      return { id, type, style: 'bullets', items: [''] };
    case 'callout':
      return { id, type, tone: 'info', text: '' };
    case 'image':
      return { id, type, image_key: '' };
    case 'pdf':
      return { id, type, file_key: '' };
    case 'video':
      return { id, type, file_key: '' };
    case 'quote':
      return { id, type, text: '' };
    case 'statement':
      return { id, type, text: '' };
    case 'table':
      return { id, type, header: ['Column 1', 'Column 2'], rows: [['', '']] };
    case 'divider':
      return { id, type };
    case 'columns':
      return { id, type, left: [makeBlock('text') as SlideLeafBlock], right: [] };
  }
}

export function moveBlock<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const to = index + dir;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item!);
  return next;
}

/**
 * Convert a legacy body/image slide to blocks, mapping the Phase 1 markup
 * one-to-one (paragraph -> text, lists -> list, callout -> info callout,
 * heading -> heading) and the slide image to an image block. The legacy
 * fields are cleared so there is a single source of truth afterwards.
 */
export function convertSlideToBlocks(slide: TrainingSlideDef): TrainingSlideDef {
  const blocks: SlideContentBlock[] = [];
  for (const parsed of parseSlideBody(slide.body ?? '')) {
    switch (parsed.type) {
      case 'heading':
        blocks.push({ id: genId('blk'), type: 'heading', text: parsed.text });
        break;
      case 'paragraph':
        blocks.push({ id: genId('blk'), type: 'text', text: parsed.text });
        break;
      case 'bullets':
        blocks.push({ id: genId('blk'), type: 'list', style: 'bullets', items: parsed.items });
        break;
      case 'numbered':
        blocks.push({ id: genId('blk'), type: 'list', style: 'numbered', items: parsed.items });
        break;
      case 'callout':
        blocks.push({ id: genId('blk'), type: 'callout', tone: 'info', text: parsed.text });
        break;
    }
  }
  if (slide.image_key) {
    blocks.push({
      id: genId('blk'),
      type: 'image',
      image_key: slide.image_key,
      ...(slide.image_layout === 'full' ? { size: 'full' as const } : {}),
      ...(slide.image_size && slide.image_layout !== 'full' ? { size: slide.image_size } : {}),
      ...(slide.image_caption ? { caption: slide.image_caption } : {}),
      ...(slide.image_alt ? { alt: slide.image_alt } : {}),
    });
  }
  if (blocks.length === 0) blocks.push(makeBlock('text'));
  return {
    id: slide.id,
    type: 'slide',
    ...(slide.title ? { title: slide.title } : {}),
    blocks,
  };
}
